import { useCallback, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { FormikProps } from 'formik';
import { IWorkflowStep } from '@modules/admin/AdminTools/Workflows/types';
import { DealUserTypeEnum, DealUserTypeLabel } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { countRowsInUploadFile, getOptions } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal, IAlertMessage } from '@shared/components/modals/AlertModal';
import { ISelectFieldProps, SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { Wrapper } from '@styles/CommonStyles';
import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { DEAL_USERS_OPTIONS } from '@shared/constants/lists';
import { IUserBase } from '@shared/interfaces';
import { validateDealMoveImportResponse } from '../ChangeFundedDate/ChangeFundedDate';
import { requestBulkDealUserUpdateMoveImport, requestGetWorkflowStepsByWorkflowId, requestWorkFlowStepDealMoveImport } from './requests';
import { IChangeWorkFlowStep, IReassignDealUser, IWorkflow } from './interfaces';
import { UploadRow } from './components/UploadRow';
import { changeWorkflowStepSchema, reassignDealUserSchema } from './validations';

const changeWorkflowInitialValues = {
  document: new File([], ''),
  workflowId: '',
  workflowStepId: '',
};

const reassignDealUserInitialValues = {
  document: new File([], ''),
  dealUser: '',
  assignTo: ''
};

export const ChangeWorkflowStep = () => {
  const { 
    isOpen: isOpenConfirmModal, 
    onCancel, 
    onConfirm, 
    openDialog,
  } = useConfirmationModal();

  const [workflowStepOptions, setWorkflowStepOptions] = useState<IWorkflowStep[]>([]);
  const [selectedDealUser, setDealUser] = useState<DealUserTypeEnum>();
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModalText, setConfirmModalText] = useState('');
  const [alertMessages, setAlertMessages] = useState<IAlertMessage[]>([]);

  const { data: fetchedWorkflows = [] } = useSWR<IWorkflow[]>('/workflow-service/workflows/all');
  const { data: fetchedRoleAssignments } = useSWR<{ rolesUsers: IUserBase[] }>(selectedDealUser && `/user-service/roleAssignment/roleByName?name=${DealUserTypeLabel[selectedDealUser]}`); 

  const reassignDealUserFormikRef = useRef<FormikProps<IReassignDealUser>>(null);

  const roleAssignments = useMemo(
    () => {
      if (fetchedRoleAssignments) {
        return getOptions(fetchedRoleAssignments.rolesUsers, 'userId', 'userName');
      }

      return [];
    },
    [fetchedRoleAssignments]
  );

  const workflowOption = useMemo(() => {
    const option: IWorkflow[] = [];
    fetchedWorkflows.forEach(async(workflow) => {
      if (workflow.name === 'DMV Packet Sent') {
        option.push(workflow);
        const workflowSteps = await requestGetWorkflowStepsByWorkflowId(workflow.id);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        workflowSteps.forEach((workflowStep: IWorkflowStep) => {
          if (workflowStep.name === 'Lender Confirmed') {
            const stepOption = [];
            stepOption.push(workflowStep);
            setWorkflowStepOptions(stepOption);
          }
        });
      }
    });

    return option;
  }, [fetchedWorkflows]);

  const handleSubmitChangeWorkflow = useCallback(
    async (data: IChangeWorkFlowStep) => {
      setConfirmModalText('Are you sure you want to edit these deals to "LENDER CONFIRMED"?');
      const result = await openDialog();

      if (result) {
        setIsLoading(true);

        const response = await requestWorkFlowStepDealMoveImport(data.document, data.workflowStepId);
        setIsLoading(false);

        if (response) {
          const formattedResponse = validateDealMoveImportResponse(response);
          setAlertMessages(formattedResponse);
          setShowAlertModal(true);
        } else {
          setAlertMessages([]);
          setShowAlertModal(false);
        }
      }
    },
    []
  );

  const handleSubmitReassignDealUser = useCallback(
    async (data: IReassignDealUser) => {
      const selectedUser = fetchedRoleAssignments?.rolesUsers.find(user => user.userId === data.assignTo);

      if (selectedUser) {
        const dealsCount = await countRowsInUploadFile(data.document);
        const dealUserTypeLabel = DealUserTypeLabel[data.dealUser  as unknown as DealUserTypeEnum];

        setConfirmModalText(`Are you sure you want to change the ${dealUserTypeLabel} User on ${dealsCount} Deals to ${selectedUser?.userName} ?`);
  
        const result = await openDialog();
  
        if (result) {
          setIsLoading(true);
  
          const response = await requestBulkDealUserUpdateMoveImport(
            data.document, 
            data.dealUser as unknown as DealUserTypeEnum, 
            selectedUser.userId, 
            selectedUser.userName
          );
          setIsLoading(false);

          if (response) {
            const formattedResponse = validateDealMoveImportResponse(response);
            setAlertMessages(formattedResponse);
            setShowAlertModal(true);
          } else {
            setAlertMessages([]);
            setShowAlertModal(false);
          }
        }
      }
    },
    [fetchedRoleAssignments]
  );

  const handleChangeDealUser = useCallback(
    (value: unknown) => {
      if (selectedDealUser !== value) {
        reassignDealUserFormikRef.current?.setFieldValue('assignTo', '');
      }

      setDealUser(value as DealUserTypeEnum);
    },
    [selectedDealUser]
  );

  const changeWorkflowStepFormConfig = useMemo(
    () => [
      {
        width: '30%',
        name: 'workflowId',
        label: 'Workflow',
        options: getOptions(workflowOption),
        hideNoneOption: true,
        component: SelectAutocompleteField,
      },
      {
        width: '30%',
        name: 'workflowStepId',
        label: 'Workflow Step',
        options: getOptions(workflowStepOptions),
        hideNoneOption: true,
        component: SelectAutocompleteField,
      },
    ], 
    [workflowOption, workflowStepOptions]
  );


  const reassignDealUserFormConfig = useMemo(
    () => [
      {
        width: '30%',
        name: 'dealUser',
        label: 'Deal User',
        options: DEAL_USERS_OPTIONS,
        hideNoneOption: true,
        component: (props: JSX.IntrinsicAttributes & ISelectFieldProps) => (
          <SelectAutocompleteField {...props} onChange={handleChangeDealUser} />
        ),
      },
      {
        width: '30%',
        name: 'assignTo',
        label: 'Assign To',
        options: roleAssignments,
        hideNoneOption: true,
        component: SelectAutocompleteField
      },
    ], 
    [roleAssignments, handleChangeDealUser]
  );

  return (
    <Wrapper>
      <LoaderBackdrop 
        open={isLoading} 
        handleClose={() => setIsLoading(false)} 
      />

      <UploadRow 
        title="Change Workflow Step"
        initialValues={changeWorkflowInitialValues}
        validationSchema={changeWorkflowStepSchema}
        formConfig={changeWorkflowStepFormConfig}
        onSubmit={handleSubmitChangeWorkflow}
      />

      <UploadRow 
        title="Reassign Deal User"
        formikRef={reassignDealUserFormikRef}
        initialValues={reassignDealUserInitialValues}
        validationSchema={reassignDealUserSchema}
        formConfig={reassignDealUserFormConfig}
        onSubmit={handleSubmitReassignDealUser}
      />

      {isOpenConfirmModal && (
        <AlertModal
          open
          title={' '}
          subtitle={confirmModalText}
          buttonText="YES" 
          cancelButtonText="Cancel"
          onClose={onCancel} 
          onConfirm={onConfirm}
        />
      )}

      {showAlertModal && 
        <AlertModal
          hideCancel
          title={' '}
          alertMessages={alertMessages}
          open={showAlertModal}
          buttonText={'Ok'}
          width="500px"
          onConfirm={() => setShowAlertModal(false)}
          onClose={() => setShowAlertModal(false)}
        />
      }
    </Wrapper>
  );
};
